import { GATEWAY_API_URL } from '@admin-panel/config/env';
import {
  IRiderCashAssignmentPass,
  IRiderControlCentreEtaResponse,
  IRiderControlCentreNavigationResponse,
  IRiderControlCentreResponse,
  IRiderControlCentreWork,
} from '@admin-panel/types';
import { axiosInstance } from './axios';

type LyskraftEnvelope<T> = {
  data?: T;
  success?: boolean;
  status?: { type?: string; message?: string; key?: string; code?: number };
};

export type RiderControlCentreSearchRequest = {
  riderIds?: Array<number>;
  facilityIds?: Array<number>;
};

export type RiderControlCentreEtaRequest = {
  targets: Array<{
    riderId: number;
    workType?: string;
    workId?: number;
    facilityId?: number;
  }>;
};

class RiderControlCentreApiClient {
  private readonly baseUrl = `${GATEWAY_API_URL}/order/rider-control-centre`;

  async search(request: RiderControlCentreSearchRequest = {}): Promise<IRiderControlCentreResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/search`, request);
    return unwrap(response.data, 'Could not load Rider Control Centre');
  }

  async work(riderId: number): Promise<Array<IRiderControlCentreWork>> {
    const response = await axiosInstance.get(`${this.baseUrl}/rider/${riderId}/work`);
    return unwrap(response.data, 'Could not load rider work') ?? [];
  }

  async navigation(riderId: number, work?: Pick<IRiderControlCentreWork, 'workType' | 'workId'>): Promise<IRiderControlCentreNavigationResponse> {
    const response = await axiosInstance.get(`${this.baseUrl}/rider/${riderId}/navigation`, {
      params: work?.workId ? { workType: work.workType, workId: work.workId } : undefined,
    });
    return unwrap(response.data, 'Could not resolve rider navigation');
  }

  async eta(request: RiderControlCentreEtaRequest): Promise<IRiderControlCentreEtaResponse> {
    const response = await axiosInstance.post(`${this.baseUrl}/eta`, request);
    return unwrap(response.data, 'Could not calculate route ETA');
  }

  /* "Allow next ride" for a rider blocked by cash held; the server checks role, reason and active shift. */
  async allowNextRide(riderId: number, reason: string): Promise<IRiderCashAssignmentPass> {
    const response = await axiosInstance.post(
      `${GATEWAY_API_URL}/order/rider-shift/rider/${riderId}/cash-assignment-pass`,
      { reason },
    );
    return unwrap(response.data, 'Could not allow the next ride');
  }
}

const unwrap = <T>(envelope: LyskraftEnvelope<T>, fallback: string): T => {
  if (envelope?.success === false || envelope?.status?.type === 'ERROR') {
    throw new Error(envelope?.status?.message || envelope?.status?.key || fallback);
  }
  return envelope?.data as T;
};

export default RiderControlCentreApiClient;
